import { useEffect, useRef } from 'react';
import { SOCIAL_ACTIONS, type PlayState } from '@london-life/contracts';
import { useCatalogue } from '../lib/content';
import type { Queued } from './use-play';

const RING = 2 * Math.PI * 15;

// the emoji in a ring that fills as the time passes (one CSS transition from where it is now to full, so nothing
// re-draws every second)
function Ring({ since, until, children }: { since: number | null; until: number | null; children: string }) {
  const arc = useRef<SVGCircleElement>(null);
  useEffect(() => {
    const circle = arc.current;
    if (!circle) return;
    circle.style.transition = 'none';
    if (!since || !until || until <= since) {
      circle.style.strokeDashoffset = String(RING);
      return;
    }
    const done = Math.min(1, Math.max(0, (Date.now() - since) / (until - since)));
    circle.style.strokeDashoffset = String(RING * (1 - done));
    const left = Math.max(0, until - Date.now());
    // the browser draws the start before the transition begins
    const frame = requestAnimationFrame(() => {
      circle.style.transition = `stroke-dashoffset ${left}ms linear`;
      circle.style.strokeDashoffset = '0';
    });
    return () => cancelAnimationFrame(frame);
  }, [since, until]);
  return (
    <span className="action-ring">
      <svg viewBox="0 0 34 34" aria-hidden="true">
        <circle className="action-ring-track" cx="17" cy="17" r="15" />
        <circle
          ref={arc}
          className="action-ring-fill"
          cx="17"
          cy="17"
          r="15"
          strokeDasharray={RING}
          strokeDashoffset={RING}
        />
      </svg>
      <span aria-hidden="true">{children}</span>
    </span>
  );
}

// What the character is doing, as Lagos Life shows it: a chip with the thing's emoji in a ring that fills, its
// name, and "× tap to cancel"; anything waiting after it is a ring of its own (tap to take it off). No seconds
// counting down, no bar.
export function ActionQueue({
  state,
  busyFor,
  queue,
  onCancel,
  className = '',
}: {
  state: PlayState | null;
  // seconds left of what it is doing (from the game loop's clock)
  busyFor: number;
  queue: Queued[];
  onCancel: (uid?: string) => void;
  className?: string;
}) {
  const content = useCatalogue();
  const doing = state?.doing && busyFor > 0 ? state.doing : null;
  if (!doing && !queue.length) return null;
  const label = state?.busyLabel ?? 'Busy';
  const social = Object.values(SOCIAL_ACTIONS).find((rule) => label.startsWith(`${rule.label} with`));
  const icon = content?.activities.find((activity) => activity.label === label)?.icon ?? social?.icon ?? '⏳';
  const until = state?.busyUntil ? new Date(state.busyUntil).getTime() : null;
  return (
    <div className={`action-queue ${className}`.trim()} aria-label="What you are doing">
      {doing && (
        <button
          type="button"
          className="action-chip current"
          aria-label={doing.cancellable ? `Cancel ${label}` : label}
          disabled={!doing.cancellable}
          onClick={() => onCancel()}
        >
          <Ring since={new Date(doing.since).getTime()} until={until}>
            {icon}
          </Ring>
          <span className="action-chip-text">
            <strong>{label}</strong>
            {doing.cancellable && <small>× tap to cancel</small>}
          </span>
        </button>
      )}
      {queue.map((item, index) => {
        const activity = content?.activity(item.activityId);
        const name = activity?.label ?? 'Next';
        return (
          <button
            key={item.uid}
            type="button"
            className="action-chip"
            aria-label={`Cancel ${name}`}
            onClick={() => onCancel(item.uid)}
          >
            <Ring since={null} until={null}>
              {activity?.icon ?? '⏳'}
            </Ring>
            {!doing && index === 0 && (
              <span className="action-chip-text">
                <strong>{name}</strong>
                <small>× tap to cancel</small>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
